import React, { useState, useEffect, useRef } from 'react';
import { X, Mail, Bell, Loader2, Save, Trash2, Plus, Send, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import ApiService from '../../services/ApiService';
import DateRangePicker from './DatePicker';
import dayjs from 'dayjs';

const ReportSettingsModal = ({ isOpen, onClose, generatePDF, onDateRangeChange, currentStartDate, currentEndDate }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sendingTest, setSendingTest] = useState(false);
    const [weeklyReport, setWeeklyReport] = useState(false);
    const [monthly_report_enabled, setMonthlyReport] = useState(false);
    const [report_plain_text_enabled, setPlainTextReport] = useState(false);
    const [report_custom_subject, setCustomSubject] = useState('');
    const [orgName, setOrgName] = useState('Your Organization');
    const [emails, setEmails] = useState([]);
    const [newEmail, setNewEmail] = useState('');
    const [manualStartDate, setManualStartDate] = useState(dayjs().subtract(30, 'day').startOf('day').toDate());
    const [manualEndDate, setManualEndDate] = useState(dayjs().endOf('day').toDate());
    const [showDatePicker, setShowDatePicker] = useState(false);
    const modalRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            fetchSettings();
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const response = await ApiService.getOrganizationData();
            if (response) {
                setOrgName(response.organization_name || 'Your Organization');
                setWeeklyReport(response.weekly_report_enabled || false);
                setMonthlyReport(response.monthly_report_enabled || false);
                setPlainTextReport(response.report_plain_text_enabled || false);
                setCustomSubject(response.report_custom_subject || '');
                setEmails(response.report_notification_emails || []);
            }
        } catch (error) {
            console.error('Failed to fetch report settings:', error);
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await ApiService.updateOrgFields({
                weekly_report_enabled: weeklyReport,
                monthly_report_enabled: monthly_report_enabled,
                report_plain_text_enabled: report_plain_text_enabled,
                report_custom_subject: report_custom_subject,
                report_notification_emails: emails
            });
            toast.success('Notification settings updated');
            onClose();
        } catch (error) {
            console.error('Failed to save report settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleSendManualReport = async () => {
        if (emails.length === 0) {
            toast.error('Please add at least one email recipient first');
            return;
        }

        setSendingTest(true);
        try {
            // Check if we need to update the date range on the dashboard first
            const needsDateUpdate =
                dayjs(manualStartDate).format('YYYY-MM-DD') !== dayjs(currentStartDate).format('YYYY-MM-DD') ||
                dayjs(manualEndDate).format('YYYY-MM-DD') !== dayjs(currentEndDate).format('YYYY-MM-DD');

            if (needsDateUpdate && onDateRangeChange) {
                toast.loading('Updating dashboard data for selected range...', { id: 'pdf-gen' });
                await onDateRangeChange(manualStartDate, manualEndDate);
                // Give charts some time to render
                await new Promise(resolve => setTimeout(resolve, 5000));
            }

            let pdfBlob = null;
            if (generatePDF) {
                try {
                    toast.loading('Generating PDF report...', { id: 'pdf-gen' });
                    pdfBlob = await generatePDF();
                    toast.success('PDF generated', { id: 'pdf-gen' });
                } catch (err) {
                    console.error('Failed to generate PDF for manual report:', err);
                    toast.error('Failed to generate PDF', { id: 'pdf-gen' });
                }
            }

            const payload = {
                start_date: dayjs(manualStartDate).format('YYYY-MM-DD'),
                end_date: dayjs(manualEndDate).format('YYYY-MM-DD'),
                report_type: 'manual'
            };

            await ApiService.sendTestReport(pdfBlob, 'manual', payload);
            toast.success('Manual report sent successfully!');
        } catch (error) {
            console.error('Failed to send manual report:', error);
            toast.error('Failed to send report', { id: 'pdf-gen' });
        } finally {
            setSendingTest(false);
        }
    };

    const addEmail = () => {
        if (!newEmail) return;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newEmail)) {
            toast.error('Please enter a valid email address');
            return;
        }

        if (emails.includes(newEmail)) {
            toast.error('Email already added');
            return;
        }

        setEmails([...emails, newEmail]);
        setNewEmail('');
    };

    const removeEmail = (emailToRemove) => {
        setEmails(emails.filter(email => email !== emailToRemove));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addEmail();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" onClick={onClose}>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm" aria-hidden="true"></div>

            {/* Modal */}
            <div
                ref={modalRef}
                className="relative z-[10000] w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header - Fixed */}
                <div className="bg-black px-6 py-4 rounded-t-2xl flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-white">
                            <div className="p-2 bg-white/20 rounded-lg">
                                <Bell className="h-5 w-5" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold">Report Notifications</h3>
                                <p className="text-xs text-gray-300">Configure your automated reports</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-all"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-10">
                            <Loader2 className="h-8 w-8 text-black animate-spin mb-2" />
                            <p className="text-sm text-gray-500 font-medium">Loading settings...</p>
                        </div>
                    ) : (
                        <>
                            {/* Toggles Section */}
                            <div className="space-y-4">
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Frequency</h4>

                                <div className="flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-gray-50/50">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center text-black">
                                            <span className="font-bold text-sm">W</span>
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-900">Weekly Reports</p>
                                            <p className="text-[11px] text-gray-500">Summary of the last 7 days</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setWeeklyReport(!weeklyReport)}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${weeklyReport ? 'bg-black' : 'bg-gray-200'}`}
                                    >
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${weeklyReport ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>

                                <div className="flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-gray-50/50">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center text-black">
                                            <span className="font-bold text-sm">M</span>
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-900">Monthly Reports</p>
                                            <p className="text-[11px] text-gray-500">Deep dive into last month&rsquo;s data</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setMonthlyReport(!monthly_report_enabled)}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${monthly_report_enabled ? 'bg-black' : 'bg-gray-200'}`}
                                    >
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${monthly_report_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>

                                <div className="flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-gray-50/50">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center text-black">
                                            <Mail className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-900">Plain Text Email Body</p>
                                            <p className="text-[11px] text-gray-500">Send simple text instead of HTML template</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setPlainTextReport(!report_plain_text_enabled)}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${report_plain_text_enabled ? 'bg-black' : 'bg-gray-200'}`}
                                    >
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${report_plain_text_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>
                            </div>

                            {/* Custom Subject Section */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Custom Subject</h4>
                                    {!report_custom_subject && (
                                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tight">Using Default</span>
                                    )}
                                </div>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={report_custom_subject || `${orgName} | Customer Query Report – {{report_period}}`}
                                        onChange={(e) => setCustomSubject(e.target.value)}
                                        placeholder="Enter custom subject..."
                                        className={`block w-full px-4 py-2.5 text-sm border-2 rounded-xl focus:border-black focus:ring-0 transition-all ${!report_custom_subject ? 'border-gray-200 bg-gray-50 text-gray-500' : 'border-gray-100 bg-white text-gray-900'}`}
                                    />
                                </div>
                                <p className="text-[10px] text-gray-400 leading-tight px-1">
                                    Use <code className="text-black">{"{{org name}}"}</code> and <code className="text-black">{"{{report_period}}"}</code> as dynamic placeholders.
                                </p>
                            </div>

                            {/* Email List Section */}
                            <div className="space-y-4">
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Recipients</h4>

                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="email"
                                        value={newEmail}
                                        onChange={(e) => setNewEmail(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="Add email address..."
                                        className="block w-full pl-10 pr-12 py-2.5 text-sm border-2 border-gray-100 rounded-xl focus:border-black focus:ring-0 transition-all placeholder:text-gray-400"
                                    />
                                    <button
                                        onClick={addEmail}
                                        className="absolute right-2 top-1.5 p-1 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors shadow-sm"
                                    >
                                        <Plus className="h-5 w-5" />
                                    </button>
                                </div>

                                <div className="space-y-2 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                                    {emails.length === 0 ? (
                                        <div className="text-center py-4 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                            <p className="text-xs text-gray-400">No email addresses added yet</p>
                                        </div>
                                    ) : (
                                        emails.map((email, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center justify-between p-2.5 bg-white border border-gray-100 rounded-lg group hover:border-blue-200 transition-all"
                                            >
                                                <span className="text-xs font-medium text-gray-700 truncate">{email}</span>
                                                <button
                                                    onClick={() => removeEmail(email)}
                                                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all opacity-0 group-hover:opacity-100"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>

                                {/* Manual Report Section */}
                                <div className="pt-4 border-t border-gray-100">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Manual Report</h4>
                                        <button
                                            onClick={() => setShowDatePicker(!showDatePicker)}
                                            className="text-[11px] font-bold text-black hover:underline flex items-center gap-1"
                                        >
                                            <Calendar className="w-3 h-3" />
                                            {showDatePicker ? 'Hide Date Picker' : 'Select Date Range'}
                                        </button>
                                    </div>

                                    {showDatePicker && (
                                        <div className="mb-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
                                            <DateRangePicker
                                                startDate={manualStartDate}
                                                endDate={manualEndDate}
                                                onChange={(start, end) => {
                                                    setManualStartDate(start);
                                                    setManualEndDate(end);
                                                }}
                                                className="w-full"
                                            />
                                            <p className="text-[10px] text-gray-500 mt-2 italic">
                                                Note: Ensure the dashboard view matches this range before sending.
                                            </p>
                                        </div>
                                    )}

                                    <button
                                        onClick={handleSendManualReport}
                                        disabled={loading || sendingTest || emails.length === 0}
                                        className="w-full flex items-center justify-center gap-2 py-3 text-sm font-bold text-black bg-gray-100 border-2 border-gray-200 rounded-xl hover:bg-gray-200 hover:border-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                                    >
                                        {sendingTest ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Send className="h-4 w-4" />
                                        )}
                                        Send Manual Report Now
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer - Fixed */}
                <div className="bg-gray-50 px-6 py-4 flex gap-3 rounded-b-2xl flex-shrink-0 border-t border-gray-100">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2.5 text-sm font-bold text-gray-600 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={loading || saving}
                        className="flex-[2] flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-black rounded-xl hover:bg-gray-800 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Saving...</span>
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4" />
                                <span>Save Settings</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: #f1f5f9;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }
            `}</style>
        </div>
    );
};

export default ReportSettingsModal;
